
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Users, User, CheckCircle2, Search } from 'lucide-react';

interface MultiPersonSelectorProps {
  submissions: Array<{
    id: string;
    full_name: string;
    email: string;
    completed_at: string;
  }>;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onAnalyze: () => void;
  loading?: boolean;
  analysisEnabled?: boolean;
  customAnalyzeText?: string;
  showAnalyzeButton?: boolean;
}

const MultiPersonSelector: React.FC<MultiPersonSelectorProps> = ({
  submissions,
  selectedIds,
  onSelectionChange,
  onAnalyze,
  loading = false,
  analysisEnabled = true,
  customAnalyzeText,
  showAnalyzeButton = true
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSubmissions = useMemo(() => {
    if (!searchQuery.trim()) return submissions;
    
    const query = searchQuery.toLowerCase();
    return submissions.filter(person => 
      person.full_name.toLowerCase().includes(query) ||
      person.email.toLowerCase().includes(query)
    );
  }, [submissions, searchQuery]);

  const handlePersonToggle = (personId: string) => {
    if (selectedIds.includes(personId)) {
      onSelectionChange(selectedIds.filter(id => id !== personId));
    } else if (selectedIds.length < 8) {
      onSelectionChange([...selectedIds, personId]);
    }
  };

  const canSelectMore = selectedIds.length < 8;
  const canAnalyze = selectedIds.length >= 3 && selectedIds.length <= 8 && analysisEnabled;

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Select Team Members ({selectedIds.length}/8)
          </CardTitle>
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            {selectedIds.length >= 3 ? `${selectedIds.length} selected` : `Need ${3 - selectedIds.length} more`}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Choose 3-8 team members for comprehensive team analysis
        </p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search team members by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="grid gap-3 max-h-96 overflow-y-auto">
          {filteredSubmissions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                {searchQuery ? 'No team members found matching your search.' : 'No team members available.'}
              </p>
            </div>
          ) : (
            filteredSubmissions.map(person => {
              const isSelected = selectedIds.includes(person.id);
              const canSelect = canSelectMore || isSelected;
              
              return (
                <div
                  key={person.id}
                  className={`flex items-center space-x-3 p-3 rounded-lg border transition-all ${
                    isSelected 
                      ? 'border-blue-200 bg-blue-50' 
                      : canSelect 
                        ? 'border-border hover:border-border hover:bg-muted' 
                        : 'border-border bg-gray-25 opacity-50'
                  } ${canSelect ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                  onClick={() => canSelect && handlePersonToggle(person.id)}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={!canSelect}
                    className="h-4 w-4"
                  />
                  
                  <div className="flex items-center gap-2 flex-1">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <div className="font-medium text-foreground">{person.full_name}</div>
                      <div className="text-sm text-muted-foreground">{person.email}</div>
                    </div>
                  </div>
                  
                  {isSelected && (
                    <CheckCircle2 className="h-4 w-4 text-blue-600" />
                  )}
                </div>
              );
            })
          )}
        </div>

        {!canSelectMore && (
          <div className="text-center py-2">
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              Maximum 8 team members selected
            </Badge>
          </div>
        )}

        {showAnalyzeButton && (
          <>
            <div className="flex justify-center pt-4 border-t">
              <Button
                onClick={onAnalyze}
                disabled={!canAnalyze || loading}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90 text-white font-medium"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Analyzing...
                  </div>
                ) : (
                  customAnalyzeText || 'Analyze Team'
                )}
              </Button>
            </div>

            {selectedIds.length < 3 && (
              <div className="text-center text-sm text-muted-foreground">
                Select at least 3 team members to enable analysis
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MultiPersonSelector;
